var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethodSource } from "healthex-common/account-linking.dtos";
import { getPrimaryIASConsentType } from "healthex-common/consent.dtos";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { formatUSPhoneForDisplay } from "healthex-common/phone.number.utils";
import { buildWebsiteUrl, WebsiteURL } from "healthex-common/websiteurls";
import { computed, observable, runInAction, when } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getObservableT, i18nGetCurrentLanguage } from "../Infra/i18next";
import { AddContactMethodOTPManager, AddContactStep } from "./AddContactMethodOTPManager";
import { FormModel } from "./FormModel";
import { HXMenuController } from "./HXMenu";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
const t = getObservableT("patients");
export class PatientProfileViewModel {
    constructor(container) {
        this.formModel = new FormModel({ selectedContactId: null }, []);
        this.howHealthExUsesYourRecordsOpen = false;
        this.viewingAccessConsentFaqs = false;
        this.accessConsentEvaluation = NOT_LOADED;
        this.smsOptInDialogModel = new SimpleDialogModel();
        this.smsOptInConfirmDialogModel = new SimpleDialogModel();
        this.profileMoreMenuController = new HXMenuController();
        this.smsOptInPollApiTimerId = null;
        this.smsOptInDialogOpenLimitTimerId = null;
        this.pendingPhoneForSmsOptIn = null;
        this.isSavingContactSelection = false;
        this.isAvatarUploading = false;
        this.removeConfirmPending = null;
        this.cantRemoveLastPhonePending = null;
        this.isRemovingContact = false;
        this.isDismissingContactPrompt = false;
        this.capturedContactOrder = null;
        this.contactOrderCaptureDisposer = null;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.appUIManager = container.appUIManager;
        this.patientContactsUIManager = container.patientContactsUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.bannerUIManager = container.bannerUIManager;
        this.environment = container.environment;
        this.patientService = container.patientService;
        this.provisioningService = container.provisioningService;
        this.embeddedManager = container.embeddedManager;
        this.addContactOTPManager = new AddContactMethodOTPManager(this.patientService, () => this.authManager.id, "email");
    }
    run() {
        this.loadAccessConsentEvaluation();
    }
    stop() {
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
        this.disposeContactOrderCapture();
        this.addContactOTPManager.clearResendTimers();
    }
    get showLogout() {
        return !this.embeddedManager.embedded;
    }
    get orgName() {
        return this.authManager.orgName;
    }
    get name() {
        return this.authManager.name;
    }
    get userForAvatar() {
        return this.authManager.userForAvatar;
    }
    get email() {
        return this.authManager.email;
    }
    get phone() {
        return this.authManager.formattedPhone;
    }
    get preferredContactMethodRemote() {
        return this.patientContactsUIManager.preferredContactMethod;
    }
    get isOptedIn() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return !((_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false);
    }
    get smsOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return false;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.smsOptedOut) !== null && _b !== void 0 ? _b : false;
    }
    get phoneNumberSuffix() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.phoneNumberSuffix) !== null && _b !== void 0 ? _b : null;
    }
    get twilioFromPhoneNumber() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.twilioFromPhoneNumber) !== null && _b !== void 0 ? _b : null;
    }
    get showPreferredContacts() {
        if (this.isOptedIn === NOT_LOADED)
            return NOT_LOADED;
        return this.isOptedIn && (!!this.email || !!this.phone);
    }
    get showUnconfirmedContactPrompt() {
        if (this.showPreferredContacts !== true)
            return false;
        const prefs = this.patientPreferencesManager.preferences.value;
        if (prefs === NOT_LOADED || !prefs)
            return false;
        return !prefs.preferredContactMethodConfirmed && !prefs.preferredContactPromptDismissed;
    }
    async dismissUnconfirmedContactPrompt() {
        if (this.isDismissingContactPrompt)
            return;
        runInAction(() => {
            this.isDismissingContactPrompt = true;
        });
        try {
            await this.patientPreferencesManager.updatePatientPreferences({ preferredContactPromptDismissed: true });
        }
        catch (e) {
            console.error("Failed to dismiss contact preference prompt", e);
            this.bannerUIManager.displayBanner({
                text: t("Failed to update your contact method. Please try again"),
                type: "ERROR",
            });
        }
        finally {
            runInAction(() => {
                this.isDismissingContactPrompt = false;
            });
        }
    }
    get isClearLogin() {
        return this.authManager.gfidProvider === GFIDProvider.CLEAR;
    }
    get contactsLoading() {
        return this.patientPreferencesManager.preferencesWithContacts.value === NOT_LOADED;
    }
    get backendEmails() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return (_a = prefs.emails) !== null && _a !== void 0 ? _a : [];
    }
    get backendPhones() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return (_a = prefs.phones) !== null && _a !== void 0 ? _a : [];
    }
    enterContactPreferences() {
        if (this.showUnconfirmedContactPrompt) {
            void this.dismissUnconfirmedContactPrompt();
        }
        if (this.capturedContactOrder !== null)
            return;
        this.disposeContactOrderCapture();
        this.contactOrderCaptureDisposer = when(() => this.patientPreferencesManager.preferencesWithContacts.value !== NOT_LOADED, () => this.captureContactOrder());
    }
    leaveContactPreferences() {
        this.disposeContactOrderCapture();
        runInAction(() => {
            this.capturedContactOrder = null;
            this.removeConfirmPending = null;
            this.cantRemoveLastPhonePending = null;
            this.isRemovingContact = false;
        });
    }
    captureContactOrder() {
        const contactIds = [...this.backendEmails, ...this.backendPhones].map((contact) => contact.id);
        runInAction(() => {
            this.capturedContactOrder = contactIds;
        });
    }
    disposeContactOrderCapture() {
        if (this.contactOrderCaptureDisposer !== null) {
            this.contactOrderCaptureDisposer();
            this.contactOrderCaptureDisposer = null;
        }
    }
    inCapturedOrder(contacts) {
        const capturedOrder = this.capturedContactOrder;
        if (capturedOrder === null)
            return contacts;
        const captured = capturedOrder
            .map((id) => contacts.find((contact) => contact.id === id))
            .filter((contact) => contact !== undefined);
        return [...captured, ...contacts.filter((contact) => !capturedOrder.includes(contact.id))];
    }
    get emails() {
        return this.inCapturedOrder(this.backendEmails);
    }
    get phones() {
        return this.inCapturedOrder(this.backendPhones);
    }
    get hasMultipleContacts() {
        return this.emails.length + this.phones.length > 1;
    }
    get preferredContactMethodForSelection() {
        const preferences = this.patientPreferencesManager.preferences.value;
        if (preferences !== NOT_LOADED && (preferences === null || preferences === void 0 ? void 0 : preferences.preferredContactMethod)) {
            return preferences.preferredContactMethod;
        }
        const remotePreference = this.preferredContactMethodRemote;
        return remotePreference === NOT_LOADED ? null : remotePreference;
    }
    get savedPreferredContactId() {
        const preferredContactMethod = this.preferredContactMethodForSelection;
        const primaryEmail = this.emails.find((e) => e.isPrimary);
        if (primaryEmail && preferredContactMethod === ContactMethod.EMAIL) {
            return primaryEmail.id;
        }
        const primaryPhone = this.phones.find((p) => p.isPrimary);
        if (primaryPhone && preferredContactMethod === ContactMethod.TEXT) {
            return primaryPhone.id;
        }
        if (primaryEmail)
            return primaryEmail.id;
        if (primaryPhone)
            return primaryPhone.id;
        return null;
    }
    get protectedPreferredContactId() {
        const preferences = this.patientPreferencesManager.preferences.value;
        if (preferences === NOT_LOADED || !(preferences === null || preferences === void 0 ? void 0 : preferences.preferredContactMethod)) {
            return null;
        }
        const preferredContactMethod = preferences.preferredContactMethod;
        const primaryEmail = this.emails.find((e) => e.isPrimary);
        if (primaryEmail && preferredContactMethod === ContactMethod.EMAIL) {
            return primaryEmail.id;
        }
        const primaryPhone = this.phones.find((p) => p.isPrimary);
        if (primaryPhone && preferredContactMethod === ContactMethod.TEXT) {
            return primaryPhone.id;
        }
        return null;
    }
    get currentSelectedContactId() {
        if (this.formModel.input.selectedContactId !== null) {
            return this.formModel.input.selectedContactId;
        }
        return this.savedPreferredContactId;
    }
    selectContact(contactId) {
        if (contactId === this.currentSelectedContactId || this.isSavingContactSelection)
            return;
        this.formModel.updateInput({ selectedContactId: contactId }, false);
        return this.saveSelectedContact();
    }
    discardContactSelection() {
        this.formModel.updateInput({ selectedContactId: null }, false);
    }
    async saveSelectedContact() {
        const contactId = this.formModel.input.selectedContactId;
        if (contactId === null)
            return true;
        return await this.saveContactSelection(contactId);
    }
    startAddContact(contactType = "email") {
        this.addContactOTPManager.setContactType(contactType);
        this.addContactOTPManager.reset();
    }
    cancelAddContact() {
        this.addContactOTPManager.reset();
    }
    switchAddContactType(contactType) {
        this.addContactOTPManager.setContactType(contactType);
    }
    get addContactType() {
        return this.addContactOTPManager.contactType;
    }
    async sendAddContactCode() {
        await this.addContactOTPManager.sendVerificationCode();
    }
    async verifyAddContactCode() {
        const ok = await this.addContactOTPManager.submitVerificationCode();
        if (!ok)
            return false;
        try {
            await this.patientPreferencesManager.reloadContacts();
        }
        catch (err) {
            console.error("Failed to reload contacts after adding contact method", err);
        }
        if (this.addContactOTPManager.step !== AddContactStep.VERIFY_OTP)
            return false;
        const addedEmail = this.addContactOTPManager.addedEmail;
        const addedPhone = this.addContactOTPManager.addedPhone;
        runInAction(() => {
            this.formModel.updateInput({ selectedContactId: null }, false);
        });
        this.bannerUIManager.displayBanner({
            text: addedEmail
                ? t("{{email}} set as contact preference", { email: addedEmail.email })
                : addedPhone
                    ? t("{{phone}} set as contact preference", { phone: formatUSPhoneForDisplay(addedPhone.phoneNumber) })
                    : t("Your contact method has been set as contact preference"),
            durationInMS: 6000,
        });
        return true;
    }
    backFromAddContactOtp() {
        this.addContactOTPManager.backToEnterContact();
    }
    get addContactStep() {
        return this.addContactOTPManager.step;
    }
    canShowRemoveForEmail(email) {
        return this.isSelfAdded(email.source) && email.id !== this.protectedPreferredContactId;
    }
    canShowRemoveForPhone(phone) {
        return this.isSelfAdded(phone.source) && (phone.id !== this.protectedPreferredContactId || this.isOnlyPhoneOnAccount);
    }
    get isOnlyPhoneOnAccount() {
        return this.phones.length === 1;
    }
    isPhoneRemoveMuted(phone) {
        return this.canShowRemoveForPhone(phone) && this.isOnlyPhoneOnAccount;
    }
    requestRemoveEmail(email) {
        if (!this.canShowRemoveForEmail(email))
            return;
        runInAction(() => {
            this.cantRemoveLastPhonePending = null;
            this.removeConfirmPending = { kind: "email", id: email.id, displayValue: email.email };
        });
    }
    requestRemovePhone(phone, displayValue) {
        if (!this.canShowRemoveForPhone(phone))
            return;
        if (this.isPhoneRemoveMuted(phone)) {
            runInAction(() => {
                this.removeConfirmPending = null;
                this.cantRemoveLastPhonePending = { kind: "cant_remove_last_phone" };
            });
            return;
        }
        runInAction(() => {
            this.cantRemoveLastPhonePending = null;
            this.removeConfirmPending = { kind: "phone", id: phone.id, displayValue };
        });
    }
    closeRemoveConfirm() {
        if (this.isRemovingContact)
            return;
        runInAction(() => {
            this.removeConfirmPending = null;
        });
    }
    closeCantRemoveLastPhone() {
        runInAction(() => {
            this.cantRemoveLastPhonePending = null;
        });
    }
    async confirmRemoveContact() {
        if (this.isRemovingContact)
            return;
        const pending = this.removeConfirmPending;
        if (!pending)
            return;
        runInAction(() => {
            this.isRemovingContact = true;
        });
        try {
            if (pending.kind === "email") {
                await this.patientPreferencesManager.removeEmail(pending.id);
            }
            else {
                await this.patientPreferencesManager.removePhone(pending.id);
            }
            this.clearSelectedContactIfMatching(pending.id);
            this.bannerUIManager.displayBanner({
                text: t("{{contact}} removed", { contact: pending.displayValue }),
                durationInMS: 6000,
            });
            runInAction(() => {
                this.removeConfirmPending = null;
            });
        }
        catch (err) {
            console.error("Failed to remove contact method", err);
            this.bannerUIManager.displayBanner({
                text: t("Failed to update your contact method. Please try again"),
                type: "ERROR",
            });
        }
        finally {
            runInAction(() => {
                this.isRemovingContact = false;
            });
        }
    }
    isSelfAdded(source) {
        return source === ContactMethodSource.SELF_ADDED;
    }
    clearSelectedContactIfMatching(contactId) {
        if (this.formModel.input.selectedContactId !== contactId)
            return;
        runInAction(() => {
            this.formModel.updateInput({ selectedContactId: null }, false);
        });
    }
    async saveContactSelection(contactId) {
        const selectedEmail = this.emails.find((e) => e.id === contactId);
        const selectedPhone = this.phones.find((p) => p.id === contactId);
        if (!selectedEmail && !selectedPhone)
            return false;
        if (selectedPhone && this.smsOptedOut) {
            return await this.showSmsOptInDialogForContactSelection(selectedPhone);
        }
        runInAction(() => {
            this.isSavingContactSelection = true;
        });
        try {
            if (selectedEmail) {
                await this.patientPreferencesManager.setPrimaryEmail(selectedEmail.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.EMAIL });
                this.bannerUIManager.displayBanner({
                    text: t("{{email}} set as contact preference", { email: selectedEmail.email }),
                    durationInMS: 6000,
                });
            }
            else if (selectedPhone) {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.TEXT });
                this.bannerUIManager.displayBanner({
                    text: t("{{phone}} set as contact preference", {
                        phone: formatUSPhoneForDisplay(selectedPhone.phoneNumber),
                    }),
                });
            }
            return true;
        }
        catch (e) {
            console.error("Failed to save contact selection", e);
            this.bannerUIManager.displayBanner({
                text: t("Failed to update your contact method. Please try again"),
                type: "ERROR",
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.formModel.updateInput({ selectedContactId: null }, false);
                this.isSavingContactSelection = false;
            });
        }
    }
    async showSmsOptInDialogForContactSelection(selectedPhone) {
        this.pendingPhoneForSmsOptIn = selectedPhone;
        this.smsOptInDialogModel.show();
        this.smsOptInPollApi();
        this.smsOptInDialogOpenLimit();
        const confirmed = await this.smsOptInDialogModel.showAndWait();
        if (this.smsOptInConfirmDialogModel.open)
            return false;
        return await this.smsOptInDialogClosedForContactSelection(confirmed);
    }
    async showSmsOptInConfirmDialog() {
        this.smsOptInConfirmDialogModel.show();
        if (this.smsOptInDialogModel.open)
            this.smsOptInDialogModel.onChoice(false);
        const confirmed = await this.smsOptInConfirmDialogModel.showAndWait();
        await this.smsOptInDialogClosedForContactSelection(confirmed);
    }
    async smsOptInDialogClosedForContactSelection(isSuccess) {
        const selectedPhone = this.pendingPhoneForSmsOptIn;
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
        this.smsOptInDialogModel.open = false;
        this.pendingPhoneForSmsOptIn = null;
        let saved = false;
        if (isSuccess && selectedPhone) {
            try {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.TEXT });
                this.bannerUIManager.displayBanner({
                    text: t("{{phone}} set as contact preference", {
                        phone: formatUSPhoneForDisplay(selectedPhone.phoneNumber),
                    }),
                });
                saved = true;
            }
            catch (e) {
                console.error("Failed to update preferred contact method", e);
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method was unable to be updated to Phone. Please try again"),
                    type: "ERROR",
                });
            }
        }
        else {
            this.bannerUIManager.displayBanner({
                text: t("Your contact method was unable to be updated to Phone. Please try again"),
                type: "ERROR",
            });
        }
        runInAction(() => {
            this.formModel.updateInput({ selectedContactId: null }, false);
        });
        return saved;
    }
    async uploadProfilePhoto(file) {
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        if (file.size > PatientProfileViewModel.MAX_AVATAR_SIZE_BYTES) {
            this.bannerUIManager.displayBanner({ text: t("File size must be 16 MB or less"), type: "ERROR" });
            return;
        }
        runInAction(() => {
            this.isAvatarUploading = true;
        });
        try {
            const uploadInfo = await this.patientService.getPatientAvatarUploadUrl(patientId, file);
            await this.provisioningService.uploadFileToUrl(uploadInfo.signedUrl, file);
            if (uploadInfo.fileUrl) {
                await this.patientService.updatePatient(patientId, { avatarUrl: uploadInfo.fileUrl });
            }
            try {
                await this.authManager.refreshTokenInfo();
            }
            catch (e) {
                console.error("Failed to refresh token after avatar upload", e);
            }
        }
        catch (e) {
            console.error("Failed to upload profile photo", e);
            this.bannerUIManager.displayBanner({ text: t("Failed to upload your profile photo. Please try again"), type: "ERROR" });
        }
        finally {
            runInAction(() => {
                this.isAvatarUploading = false;
            });
        }
    }
    goToTermsOfUse() {
        window.open(buildWebsiteUrl(WebsiteURL.TERMS, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    goToPrivacyPolicy() {
        window.open(buildWebsiteUrl(WebsiteURL.PRIVACY, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    get accessConsentGrantDate() {
        var _a;
        if (this.accessConsentEvaluation === NOT_LOADED || this.accessConsentEvaluation === null)
            return null;
        const rawTimestamp = (_a = this.accessConsentEvaluation.consentRecord) === null || _a === void 0 ? void 0 : _a.timestamp;
        if (!rawTimestamp)
            return null;
        const parsed = new Date(rawTimestamp);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    get hasCompletedAccessConsent() {
        if (this.accessConsentEvaluation === NOT_LOADED || this.accessConsentEvaluation === null)
            return false;
        return this.accessConsentEvaluation.hasPatientConsented;
    }
    loadAccessConsentEvaluation() {
        if (this.accessConsentEvaluation !== NOT_LOADED)
            return;
        if (this.authManager.id === null)
            return;
        void this.patientService.hasPatientConsentedToIAS(this.authManager.id, getPrimaryIASConsentType(), true).then((evaluation) => {
            runInAction(() => {
                this.accessConsentEvaluation = evaluation;
            });
        });
    }
    openHowHealthExUsesYourRecords() {
        this.howHealthExUsesYourRecordsOpen = true;
        this.loadAccessConsentEvaluation();
    }
    closeHowHealthExUsesYourRecords() {
        this.howHealthExUsesYourRecordsOpen = false;
        this.viewingAccessConsentFaqs = false;
    }
    showAccessConsentFaqs() {
        this.viewingAccessConsentFaqs = true;
    }
    hideAccessConsentFaqs() {
        this.viewingAccessConsentFaqs = false;
    }
    async logout() {
        await this.appUIManager.logout();
    }
    logoOnClick() {
        var _a;
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION.routeName) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT);
        }
        else {
            this.appUIManager.navigateToHome();
        }
    }
    clearSmsOptInPollApi() {
        if (this.smsOptInPollApiTimerId !== null) {
            window.clearTimeout(this.smsOptInPollApiTimerId);
        }
    }
    calculateSmsOptInPollInterval(retries) {
        if (retries === 0)
            return 1000;
        if (retries === 1)
            return 10000;
        if (retries === 2)
            return 7500;
        if (retries < 10)
            return 5000;
        if (retries < 20)
            return 7500;
        if (retries < 30)
            return 10000;
        return 12000;
    }
    smsOptInPollApi(retries = 0) {
        this.clearSmsOptInPollApi();
        this.smsOptInPollApiTimerId = window.setTimeout(async () => {
            await this.patientPreferencesManager.preferences.waitForReload();
            if (!this.smsOptedOut) {
                this.smsOptInDialogModel.onChoice(true);
            }
            else {
                this.smsOptInPollApi(retries + 1);
            }
        }, this.calculateSmsOptInPollInterval(retries));
    }
    clearSmsOptInDialogOpenLimit() {
        if (this.smsOptInDialogOpenLimitTimerId !== null) {
            window.clearTimeout(this.smsOptInDialogOpenLimitTimerId);
        }
    }
    smsOptInDialogOpenLimit() {
        this.smsOptInDialogOpenLimitTimerId = window.setTimeout(async () => {
            this.smsOptInDialogModel.onChoice(false);
        }, 60000 * 15);
    }
    async deleteMyAccount() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_DELETE_MY_ACCOUNT);
    }
}
PatientProfileViewModel.MAX_AVATAR_SIZE_BYTES = 16 * 1024 * 1024;
__decorate([
    observable
], PatientProfileViewModel.prototype, "howHealthExUsesYourRecordsOpen", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "viewingAccessConsentFaqs", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "accessConsentEvaluation", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isSavingContactSelection", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isAvatarUploading", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "removeConfirmPending", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "cantRemoveLastPhonePending", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isRemovingContact", void 0);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showLogout", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "orgName", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "name", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "userForAvatar", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "email", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phone", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "preferredContactMethodRemote", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isOptedIn", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "smsOptedOut", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phoneNumberSuffix", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "twilioFromPhoneNumber", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showPreferredContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showUnconfirmedContactPrompt", null);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isDismissingContactPrompt", void 0);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isClearLogin", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "contactsLoading", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "backendEmails", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "backendPhones", null);
__decorate([
    observable
], PatientProfileViewModel.prototype, "capturedContactOrder", void 0);
__decorate([
    computed
], PatientProfileViewModel.prototype, "emails", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phones", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "hasMultipleContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "preferredContactMethodForSelection", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "savedPreferredContactId", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "protectedPreferredContactId", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "currentSelectedContactId", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "addContactType", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "addContactStep", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isOnlyPhoneOnAccount", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "accessConsentGrantDate", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "hasCompletedAccessConsent", null);
